import {useState} from 'react';
import styled, {css, keyframes} from 'styled-components';
import {FELT, FeltDefs, useFeltIds} from './felt';
import {Hop, ToyShell, type MapToyProps} from './mapToys';
import {
  Beanie,
  CatFace,
  OllieFace,
  PlushPiMark,
  Sparkle,
  type Mood,
} from './feltFriends';
import {playTones, type Tone} from './toySounds';
import {
  drawChamberFind,
  focusStep,
  hillHeight,
  climbStep,
  isAtTop,
  isHighestTop,
  makeChamber,
  makeFocusError,
  makeHill,
  MAX_CLIMB_STEPS,
  muonShower,
  PRESSES_TO_FIND_ROOM,
  PYRAMID,
  topFrom,
  hillTops,
  type Chamber,
  type ChamberFind,
  type Hill,
} from './week3Climbs';

/**
 * Week 3 map toys, for the gradient ascent and maximum likelihood week: a
 * foggy mountain climbed one gradient step per press, a camera that hunts
 * for focus, and a pyramid whose hidden room shows itself to falling muons.
 * Their stories live in `week3Climbs.ts`. Like the other map toys they carry
 * no visible text; {@link ToyShell} names them and announces each change.
 */

const reducedMotion = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

/** Slides to wherever its transform puts it. */
const Glide = styled.g`
  transition: transform 0.45s cubic-bezier(0.34, 1.2, 0.64, 1);
  ${reducedMotion}
`;

/** Turns to wherever its transform points it. */
const Turn = styled.g`
  transition: transform 0.4s cubic-bezier(0.34, 1.4, 0.64, 1);
  ${reducedMotion}
`;

/** A daisy, about 16 units across. */
function Daisy() {
  return (
    <g>
      <path
        d="M 0 4 Q 1 10 0 14"
        stroke={FELT.mint}
        strokeWidth={1.4}
        fill="none"
      />
      {[0, 45, 90, 135, 180, 225, 270, 315].map(angle => (
        <ellipse
          key={angle}
          cx={0}
          cy={-4.6}
          rx={2}
          ry={3.6}
          fill="#FFFFFF"
          transform={`rotate(${angle})`}
        />
      ))}
      <circle cx={0} cy={0} r={2.8} fill={FELT.gold} />
    </g>
  );
}

/** A small treasure chest, about 16 units across. */
function TreasureChest() {
  return (
    <g>
      <rect x={-7.5} y={-1.5} width={15} height={8} rx={1} fill="#9B6B33" />
      <path d="M -7.5 -1.5 Q 0 -8 7.5 -1.5 Z" fill="#B77F3E" />
      <rect x={-7.5} y={-2.4} width={15} height={1.8} fill={FELT.gold} />
      <rect
        x={-1.3}
        y={-2.6}
        width={2.6}
        height={4}
        rx={0.6}
        fill={FELT.gold}
      />
      {[
        {x: -5, y: -6},
        {x: 4, y: -7.5},
      ].map(({x, y}) => (
        <path
          key={x}
          d={`M ${x} ${y - 2} L ${x + 0.6} ${y - 0.6} L ${x + 2} ${y} L ${x + 0.6} ${y + 0.6} L ${x} ${y + 2} L ${x - 0.6} ${y + 0.6} L ${x - 2} ${y} L ${x - 0.6} ${y - 0.6} Z`}
          fill={FELT.gold}
        />
      ))}
    </g>
  );
}

/** A curled-up sleeping cat, about 18 units across. */
function SleepingCat() {
  return (
    <g>
      <ellipse cx={1} cy={2} rx={8} ry={4.6} fill="#E8954A" />
      <path
        d="M 8.4 3.4 Q 9 7 3 6.6"
        stroke="#E8954A"
        strokeWidth={2.4}
        fill="none"
        strokeLinecap="round"
      />
      <g transform="translate(-4 -1) scale(0.55)">
        <CatFace mood="asleep" />
      </g>
      <path
        d="M 4 -6 H 6.4 L 4 -3.6 H 6.4 M 7.6 -9 H 9.2 L 7.6 -7.4 H 9.2"
        stroke={FELT.ink}
        strokeWidth={0.6}
        fill="none"
        strokeLinejoin="round"
      />
    </g>
  );
}

/** Draws a find from the pyramid's hidden room, about 18 units across. */
function Find({find}: {find: ChamberFind}) {
  switch (find.id) {
    case 'treasure':
      return <TreasureChest />;
    case 'cat':
      return <SleepingCat />;
    case 'plush-pi':
      return <PlushPiMark mood="happy" />;
    case 'ollie':
      return <OllieFace mood="happy" />;
    case 'golden-ollie':
      return <OllieFace mood="happy" isGolden />;
  }
}

/* ── Foggy mountain ─────────────────────────────────────────────────────── */

/** The mountain's foot, in the toy's 100×84 viewBox. */
const MOUNTAIN_BASE_Y = 78;

/** How far below its peak a mountain's snow reaches. */
const SNOW_DEPTH = 7;

/** Where a climbing step leaves Beanie standing. */
interface Climb {
  hill: Hill;
  /** Beanie's position. */
  x: number;
  /** Every earlier position, oldest first: one lantern-lit patch each. */
  visited: {step: number; x: number}[];
  steps: number;
  isAtTop: boolean;
}

function startClimb(): Climb {
  const {hill, start} = makeHill(Math.random);
  return {hill, x: start, visited: [], steps: 0, isAtTop: false};
}

/** The mountain's surface height on screen at `x`. */
function surfaceY({hill, x}: {hill: Hill; x: number}): number {
  return MOUNTAIN_BASE_Y - hillHeight({hill, x});
}

/**
 * The summit's shaded right-hand face, as an SVG path to clip to the
 * mountain: a slanted edge from the summit down to the foot, and
 * everything to its right.
 */
function shadedFace({hill, summit}: {hill: Hill; summit: number}): string {
  const spread = Math.min(...hill.bumps.map(bump => bump.spread));
  return [
    `M ${summit} ${surfaceY({hill, x: summit})}`,
    `L ${summit + 0.5 * spread} ${MOUNTAIN_BASE_Y + 2}`,
    `L 104 ${MOUNTAIN_BASE_Y + 2} L 104 -10 L ${summit} -10 Z`,
  ].join(' ');
}

/** A step tick whose pitch rises with the height reached. */
function stepTone(height: number): Tone[] {
  return [{frequency: 330 + height * 9, startSec: 0, durationSec: 0.12}];
}

const SUMMIT_TONES: readonly Tone[] = [523.3, 659.3, 784, 1046.5].map(
  (frequency, index) => ({frequency, startSec: index * 0.1, durationSec: 0.25}),
);

const LOWER_TOP_TONES: readonly Tone[] = [784, 622.3].map(
  (frequency, index) => ({frequency, startSec: index * 0.16, durationSec: 0.3}),
);

function climbCaption(climb: Climb): string {
  const {hill, x, steps} = climb;
  const stepWord = steps === 1 ? 'step' : 'steps';
  if (!climb.isAtTop) {
    const [before, last] = climb.visited.slice(-2).map(spot => spot.x);
    if (last === undefined && before === undefined) {
      return 'a climber with a lantern, lost in the fog';
    }
    const step = Math.abs(x - (last ?? before ?? x));
    const previous =
      last === undefined || before === undefined
        ? step
        : Math.abs(last - before);
    if (step < 0.65 * previous) {
      return `${steps} ${stepWord} uphill. The ground flattens, so the steps get shorter`;
    }
    if (step > 1.5 * previous) {
      return `${steps} ${stepWord} uphill. The ground steepens, so the steps get longer`;
    }
    return `${steps} ${stepWord} uphill, still in the fog`;
  }
  return isHighestTop({hill, x})
    ? `the summit, after ${steps} ${stepWord}! The fog lifts`
    : `a top, after ${steps} ${stepWord}. The fog lifts on a higher one`;
}

/**
 * A mountain hidden in fog. Beanie can only see the patch their lantern
 * lights, so each press is one gradient ascent step uphill, longer where
 * the slope is steep and shorter near the top. At a top the fog lifts: a
 * summit gets a flag, and sometimes it was only the lower of two tops. The
 * next press brings a new mountain.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyFoggyMountain({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [climb, setClimb] = useState<Climb>(startClimb);
  const {hill, x, visited} = climb;
  const isSummit = climb.isAtTop && isHighestTop({hill, x});
  const fogMaskId = `${ids.fuzz}-fog`;
  const mountainClipId = `${ids.fuzz}-mountain`;
  const tops = hillTops(hill);
  const outline = Array.from({length: 51}, (_, index) => {
    const px = index * 2;
    return `${px},${surfaceY({hill, x: px}).toFixed(1)}`;
  }).join(' L ');
  const mountainPath = `M 0 ${MOUNTAIN_BASE_Y} L ${outline} L 100 ${MOUNTAIN_BASE_Y} Z`;

  const step = () => {
    if (climb.isAtTop) {
      setClimb(startClimb());
      return;
    }
    const next = climbStep({hill, x});
    const steps = climb.steps + 1;
    const hasArrived = isAtTop({hill, x: next}) || steps >= MAX_CLIMB_STEPS;
    const landing = hasArrived ? topFrom({hill, x: next}) : next;
    setClimb({
      hill,
      x: landing,
      visited: [...visited, {step: climb.steps, x}],
      steps,
      isAtTop: hasArrived,
    });
    if (!hasArrived) {
      playTones({tones: stepTone(hillHeight({hill, x: landing}))});
    } else if (isHighestTop({hill, x: landing})) {
      playTones({tones: SUMMIT_TONES});
    } else {
      playTones({tones: LOWER_TOP_TONES});
    }
  };

  const beanieY = surfaceY({hill, x}) - 5.6;
  const mood: Mood = !climb.isAtTop ? 'calm' : isSummit ? 'happy' : 'surprised';
  const highest = tops.reduce((best, top) =>
    hillHeight({hill, x: top}) > hillHeight({hill, x: best}) ? top : best,
  );
  // A knoll on the ledge stays bare; snow lies only near the summit's height.
  const snowyTops = tops.filter(
    top => hillHeight({hill, x: top}) > 0.7 * hillHeight({hill, x: highest}),
  );
  // At a lower top Beanie turns to face the higher one, lantern first.
  const facing = climb.isAtTop && !isSummit && highest < x ? -1 : 1;
  const flagX = x - 7 * facing;

  return (
    <ToyShell
      isPreview={isPreview}
      label="Foggy mountain"
      caption={climbCaption(climb)}
      onPoke={step}
    >
      <svg
        viewBox="0 0 100 84"
        width={size}
        height={size * 0.84}
        overflow="visible"
        aria-hidden
      >
        <defs>
          <FeltDefs ids={ids} />
          <clipPath id={mountainClipId}>
            <path d={mountainPath} />
          </clipPath>
          <mask
            id={fogMaskId}
            maskUnits="userSpaceOnUse"
            x={-10}
            y={-10}
            width={120}
            height={104}
          >
            <rect x={-10} y={-10} width={120} height={104} fill="#fff" />
            {visited.map(spot => (
              <circle
                key={spot.step}
                cx={spot.x}
                cy={surfaceY({hill, x: spot.x})}
                r={7}
                fill="#000"
                filter={`url(#${ids.soft})`}
              />
            ))}
            <Glide style={{transform: `translate(${x}px, ${beanieY}px)`}}>
              <circle r={12} fill="#000" filter={`url(#${ids.soft})`} />
            </Glide>
          </mask>
        </defs>
        <path d={mountainPath} fill="#8E9CB8" filter={`url(#${ids.fuzz})`} />
        <g clipPath={`url(#${mountainClipId})`}>
          <path d={shadedFace({hill, summit: highest})} fill="#76849F" />
          {snowyTops.map(top => {
            const topY = surfaceY({hill, x: top});
            const zigzag = Array.from({length: 8}, (_, index) => {
              const zx = top + 14 - (index + 1) * 3.5;
              return `L ${zx.toFixed(1)} ${(topY + SNOW_DEPTH - (index % 2 === 0 ? 2.6 : 0)).toFixed(1)}`;
            }).join(' ');
            return (
              <path
                key={top}
                d={`M ${top - 14} 0 H ${top + 14} V ${topY + SNOW_DEPTH} ${zigzag} Z`}
                fill="#FFFFFF"
              />
            );
          })}
        </g>
        <path
          d={`M 0 ${MOUNTAIN_BASE_Y} L 100 ${MOUNTAIN_BASE_Y}`}
          stroke="#6F7D99"
          strokeWidth={1.2}
          strokeLinecap="round"
        />
        <FogLayer $isLifted={climb.isAtTop} mask={`url(#${fogMaskId})`}>
          <path
            d="M 0 80 Q -4 62 6 54 Q 2 34 22 32 Q 26 12 48 14 Q 66 2 80 20 Q 100 18 98 42 Q 106 60 100 80 Z"
            fill="#F7F9FC"
            stroke="#D3DAE5"
            strokeWidth={1.2}
            filter={`url(#${ids.fuzz})`}
          />
        </FogLayer>
        {visited.map(spot => (
          <circle
            key={spot.step}
            cx={spot.x}
            cy={surfaceY({hill, x: spot.x}) - 1}
            r={1.4}
            fill={FELT.gold}
            stroke="#fff"
            strokeWidth={0.5}
          />
        ))}
        {climb.isAtTop && (
          <Hop key={`flag-${x}`}>
            <g
              transform={`translate(${flagX} ${surfaceY({hill, x: flagX})}) scale(${facing} 1)`}
            >
              <path d="M 0 0 V -17" stroke={FELT.ink} strokeWidth={1} />
              <path
                d="M 0 -17 L -9 -14 L 0 -11 Z"
                fill={isSummit ? FELT.cardinal : '#B9C2D3'}
              />
            </g>
          </Hop>
        )}
        {climb.isAtTop && !isSummit && (
          <Hop key={`glint-${highest}`}>
            <Sparkle x={highest} y={surfaceY({hill, x: highest}) - 6} r={3.4} />
          </Hop>
        )}
        <Glide style={{transform: `translate(${x}px, ${beanieY}px)`}}>
          {!climb.isAtTop && (
            <circle
              cx={6.6}
              cy={-0.6}
              r={5}
              fill={FELT.gold}
              opacity={0.3}
              filter={`url(#${ids.soft})`}
            />
          )}
          <g transform={facing < 0 ? 'scale(-1 1)' : undefined}>
            <path
              d="M 3.6 2 L 6.2 -1.2"
              stroke={FELT.ink}
              strokeWidth={0.7}
              strokeLinecap="round"
            />
            <rect
              x={5.2}
              y={-1.4}
              width={2.8}
              height={3.2}
              rx={0.8}
              fill={FELT.gold}
              stroke="#B07A1E"
              strokeWidth={0.4}
            />
          </g>
          <Beanie mood={mood} />
        </Glide>
        {isSummit &&
          [
            {dx: -9, dy: -16, r: 2.2},
            {dx: 10, dy: -20, r: 2.8},
            {dx: -3, dy: -24, r: 1.8},
          ].map(({dx, dy, r}) => (
            <Hop key={`star-${dx}-${x}`}>
              <Sparkle x={x + dx} y={surfaceY({hill, x}) + dy} r={r} />
            </Hop>
          ))}
      </svg>
    </ToyShell>
  );
}

const FogLayer = styled.g<{$isLifted: boolean}>`
  opacity: ${({$isLifted}) => ($isLifted ? 0.1 : 0.94)};
  transition: opacity 0.9s ease-out;
  ${reducedMotion}
`;

/* ── Autofocus camera ───────────────────────────────────────────────────── */

/** What the camera can be pointed at. */
const SUBJECTS = [
  {id: 'ollie', name: 'Ollie'},
  {id: 'cat', name: 'a cat'},
  {id: 'daisy', name: 'a daisy'},
  {id: 'beanie', name: 'Beanie'},
] as const;

/** Something the camera can be pointed at. */
type Subject = (typeof SUBJECTS)[number];

/** Draws a subject, about 18 units across, centered at the origin. */
function SubjectPicture({subject}: {subject: Subject}) {
  switch (subject.id) {
    case 'ollie':
      return <OllieFace mood="happy" />;
    case 'cat':
      return <CatFace mood="calm" />;
    case 'daisy':
      return <Daisy />;
    case 'beanie':
      return (
        <g transform="scale(1.5)">
          <Beanie mood="happy" />
        </g>
      );
  }
}

/** The blur, in viewBox units, of a picture whose focus is off by `error`. */
function blurFor(error: number): number {
  return Math.min(4.5, 3.6 * Math.abs(error));
}

/** A camera's state: who is in frame and how far off the focus is. */
interface Shot {
  subject: Subject;
  /** Focus minus the sharp setting; 0 is sharp. */
  error: number;
  steps: number;
  /** Photos taken, so each new photo pops in afresh. */
  photos: number;
}

function nextSubject(current?: Subject): Subject {
  const others = SUBJECTS.filter(subject => subject.id !== current?.id);
  return others[Math.floor(Math.random() * others.length)] ?? SUBJECTS[0];
}

const FOCUS_TICK: readonly Tone[] = [
  {frequency: 1046.5, startSec: 0, durationSec: 0.05},
];

const SHUTTER: readonly Tone[] = [
  {frequency: 1800, startSec: 0, durationSec: 0.03},
  {frequency: 1200, startSec: 0.07, durationSec: 0.04},
  {frequency: 1318.5, startSec: 0.22, durationSec: 0.25},
];

function shotCaption(shot: Shot): string {
  if (shot.error === 0) {
    return `click! A sharp photo of ${shot.subject.name}`;
  }
  if (shot.steps === 0) {
    return `a blurry picture of ${shot.subject.name}`;
  }
  const sharpness = Math.max(0, Math.round(100 * (1 - shot.error ** 2)));
  return `focusing, ${sharpness}% sharp`;
}

/**
 * A camera that hunts for focus, like the autofocus in the gradient ascent
 * lesson: each press turns the focus toward sharp, overshooting a little
 * less each time. When the picture is sharp the shutter clicks and a photo
 * pops out. The next press points it at someone new.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyAutofocusCamera({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [shot, setShot] = useState<Shot>(() => ({
    subject: nextSubject(),
    error: makeFocusError(Math.random),
    steps: 0,
    photos: 0,
  }));
  const blurId = `${ids.fuzz}-blur`;
  const screenClipId = `${ids.fuzz}-screen`;
  const isSharp = shot.error === 0;

  const press = () => {
    if (isSharp) {
      setShot({
        subject: nextSubject(shot.subject),
        error: makeFocusError(Math.random),
        steps: 0,
        photos: shot.photos,
      });
      playTones({tones: FOCUS_TICK});
      return;
    }
    const error = focusStep({focus: shot.error, best: 0});
    setShot({
      ...shot,
      error,
      steps: shot.steps + 1,
      photos: shot.photos + (error === 0 ? 1 : 0),
    });
    playTones({tones: error === 0 ? SHUTTER : FOCUS_TICK, wave: 'triangle'});
  };

  const bracket = isSharp ? FELT.mint : '#FFFFFF';

  return (
    <ToyShell
      isPreview={isPreview}
      label="Autofocus camera"
      caption={shotCaption(shot)}
      onPoke={press}
    >
      <svg
        viewBox="0 0 100 84"
        width={size}
        height={size * 0.84}
        overflow="visible"
        aria-hidden
      >
        <defs>
          <FeltDefs ids={ids} />
          <filter id={blurId} x={-0.5} y={-0.5} width={2} height={2}>
            <feGaussianBlur stdDeviation={blurFor(shot.error)} />
          </filter>
          <clipPath id={screenClipId}>
            <rect x={20} y={40} width={60} height={32} rx={3} />
          </clipPath>
        </defs>
        {isSharp && (
          <Hop key={`photo-${shot.photos}`}>
            <g transform="translate(62 30) rotate(10)">
              <rect
                x={-13}
                y={-28}
                width={26}
                height={30}
                rx={1.2}
                fill="#FFFFFF"
                stroke="#D9D2C5"
                strokeWidth={0.6}
              />
              <rect x={-10.5} y={-25.5} width={21} height={19} fill="#CFE3F0" />
              <g transform="translate(0 -16) scale(0.62)">
                <SubjectPicture subject={shot.subject} />
              </g>
            </g>
          </Hop>
        )}
        <rect
          x={36}
          y={24}
          width={24}
          height={10}
          rx={3.5}
          fill="#3E4452"
          filter={`url(#${ids.fuzz})`}
        />
        <rect
          x={12}
          y={31}
          width={76}
          height={47}
          rx={9}
          fill="#3E4452"
          filter={`url(#${ids.fuzz})`}
        />
        <circle cx={78} cy={30.5} r={4} fill={FELT.cardinal} />
        <Turn
          style={{
            transform: `rotate(${shot.error * 140}deg)`,
            transformOrigin: '22px 30px',
          }}
        >
          <circle
            cx={22}
            cy={30}
            r={5}
            fill="#8E96A3"
            stroke="#6B7280"
            strokeWidth={0.8}
          />
          <path
            d="M 22 30 V 25.8"
            stroke="#FFFFFF"
            strokeWidth={1.2}
            strokeLinecap="round"
          />
        </Turn>
        <rect x={20} y={40} width={60} height={32} rx={3} fill="#CFE3F0" />
        <g clipPath={`url(#${screenClipId})`}>
          <path
            d="M 20 66 Q 40 61 60 64 Q 72 66 80 63 V 72 H 20 Z"
            fill="#A9CF96"
          />
          <g filter={isSharp ? undefined : `url(#${blurId})`}>
            <g transform="translate(50 56)">
              <SubjectPicture subject={shot.subject} />
            </g>
          </g>
        </g>
        {[
          {x: 37, y: 44, dx: 1, dy: 1},
          {x: 63, y: 44, dx: -1, dy: 1},
          {x: 37, y: 68, dx: 1, dy: -1},
          {x: 63, y: 68, dx: -1, dy: -1},
        ].map(({x, y, dx, dy}) => (
          <path
            key={`${x}-${y}`}
            d={`M ${x} ${y + dy * 4} V ${y} H ${x + dx * 4}`}
            stroke={bracket}
            strokeWidth={1.4}
            fill="none"
            strokeLinecap="round"
          />
        ))}
      </svg>
    </ToyShell>
  );
}

/* ── Pyramid ────────────────────────────────────────────────────────────── */

const fall = keyframes`
  0% { transform: translateY(0); opacity: 0; }
  10% { opacity: 1; }
  85% { opacity: 1; }
  100% { transform: translateY(82px); opacity: 0; }
`;

/** One muon's streak, falling once from above the pyramid to the sand. */
const Muon = styled.path<{$delaySec: number}>`
  opacity: 0;
  animation: ${fall} 0.8s linear ${({$delaySec}) => $delaySec}s 1 both;
  ${reducedMotion}
`;

/** A muon that slipped through the hidden room, marked inside it. */
interface Glint {
  id: string;
  x: number;
  y: number;
}

/** The pyramid's state: where the room is, what's in it, what's been seen. */
interface Dig {
  chamber: Chamber;
  find: ChamberFind;
  presses: number;
  /** Where this press's muons fell. */
  shower: number[];
  glints: Glint[];
}

function startDig(): Dig {
  return {
    chamber: makeChamber(Math.random),
    find: drawChamberFind(Math.random),
    presses: 0,
    shower: [],
    glints: [],
  };
}

const MUON_TONES: readonly Tone[] = [1568, 2093, 1760].map(
  (frequency, index) => ({
    frequency,
    startSec: index * 0.07,
    durationSec: 0.06,
  }),
);

const REVEAL_TONES: readonly Tone[] = [440, 523.3, 659.3, 880].map(
  (frequency, index) => ({
    frequency,
    startSec: index * 0.12,
    durationSec: 0.35,
  }),
);

function digCaption(dig: Dig): string {
  switch (dig.presses) {
    case 0:
      return 'an old pyramid, with muons raining from the sky';
    case 1:
      return 'muons rain through, and a few slip through something hollow';
    case 2:
      return 'more muons: a hidden room is taking shape';
    default:
      return `a hidden room! Inside: ${dig.find.name}`;
  }
}

/**
 * A pyramid with a hidden room, as in the Hidden Chamber assignment. Each
 * press rains muons through it; the ones that pass through the hollow room
 * leave glints, and the room takes shape until, on the third press, it
 * opens to show what it holds (rarely a golden Ollie). The next press
 * brings a new pyramid.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyPyramid({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [dig, setDig] = useState<Dig>(startDig);
  const {chamber, presses} = dig;
  const isFound = presses >= PRESSES_TO_FIND_ROOM;
  const pyramidClipId = `${ids.fuzz}-pyramid`;
  const pyramidPoints = `${PYRAMID.apex.x},${PYRAMID.apex.y} ${PYRAMID.left.x},${PYRAMID.left.y} ${PYRAMID.right.x},${PYRAMID.right.y}`;

  const press = () => {
    if (isFound) {
      setDig(startDig());
      return;
    }
    const shower = muonShower({chamber, random: Math.random});
    const inRoom = shower.filter(
      x => x > chamber.x + 1 && x < chamber.x + chamber.width - 1,
    );
    const nextPresses = presses + 1;
    setDig({
      ...dig,
      presses: nextPresses,
      shower,
      glints: [
        ...dig.glints,
        ...inRoom.map((x, index) => ({
          id: `${nextPresses}-${index}`,
          x,
          y: chamber.y + 3 + Math.random() * (chamber.height - 6),
        })),
      ],
    });
    playTones({
      tones: nextPresses >= PRESSES_TO_FIND_ROOM ? REVEAL_TONES : MUON_TONES,
    });
  };

  const roomOpacity = Math.min(1, presses / PRESSES_TO_FIND_ROOM);

  return (
    <ToyShell
      isPreview={isPreview}
      label="Pyramid"
      caption={digCaption(dig)}
      onPoke={press}
    >
      <svg
        viewBox="0 0 100 84"
        width={size}
        height={size * 0.84}
        overflow="visible"
        aria-hidden
      >
        <defs>
          <FeltDefs ids={ids} />
          <clipPath id={pyramidClipId}>
            <polygon points={pyramidPoints} />
          </clipPath>
        </defs>
        <path
          d="M -4 84 Q -2 76 18 74 Q 50 71 82 74 Q 102 75 104 84 Z"
          fill="#E9D29C"
          filter={`url(#${ids.fuzz})`}
        />
        <polygon
          points={pyramidPoints}
          fill="#E3C185"
          filter={`url(#${ids.fuzz})`}
        />
        <polygon
          points={`${PYRAMID.apex.x},${PYRAMID.apex.y} 58,${PYRAMID.right.y} ${PYRAMID.right.x},${PYRAMID.right.y}`}
          fill="#C99E5C"
          filter={`url(#${ids.fuzz})`}
        />
        <g clipPath={`url(#${pyramidClipId})`}>
          {[26, 38, 50, 62].map(y => (
            <path
              key={y}
              d={`M 0 ${y} H 100`}
              stroke="#A9803F"
              strokeWidth={0.7}
              opacity={0.45}
            />
          ))}
          <g opacity={roomOpacity}>
            <rect
              x={chamber.x}
              y={chamber.y}
              width={chamber.width}
              height={chamber.height}
              rx={1.5}
              fill={isFound ? '#FFE6A6' : '#6E4A26'}
              stroke="#7A5328"
              strokeWidth={1}
            />
          </g>
          {dig.glints.map(glint => (
            <Hop key={glint.id}>
              <Sparkle x={glint.x} y={glint.y} r={1.6} />
            </Hop>
          ))}
        </g>
        {isFound && (
          <Hop key={`find-${chamber.x}`}>
            <g
              transform={`translate(${chamber.x + chamber.width / 2} ${chamber.y + chamber.height / 2 + 0.6}) scale(0.74)`}
            >
              <Find find={dig.find} />
            </g>
          </Hop>
        )}
        {!isPreview && (
          <g key={`shower-${presses}`}>
            {dig.shower.map((x, index) => (
              <Muon
                // A muon is identified by its order in the shower.
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={index}
                d={`M ${x} -10 L ${x + 0.8} -4`}
                stroke={FELT.gold}
                strokeWidth={1.4}
                strokeLinecap="round"
                $delaySec={index * 0.05}
              />
            ))}
          </g>
        )}
      </svg>
    </ToyShell>
  );
}
